import visa from "../../assets/Images/visa.png";
import cc_generic from "../../assets/Images/cc_generic.png";
import amex from "../../assets/Images/amex.png";
import city_double_cash from "../../assets/Images/city_double_cash.png";
import check_everyday from "../../assets/Images/check_everyday.png";
import pwa_image from "../../assets/Images/pwa_image.png";
import React, { useState, useRef, useEffect,useContext } from "react";
import { Plan, SubscriptionDetail } from "../../types/types";
import { AsaStateContext } from "../../components/AsaStateProvider";
import { useTranslation } from "react-i18next";
import { usePaymentMethods, useFintechLayouts,} from "../../services/AsaApiServices";
import { useNavigate } from "react-router-dom";
import { AnalyticsService, AnalyticsAction, AnalyticsFeature } from "../../services/AnalyticsService";

interface TransferManagePlanProps {
  subscription: SubscriptionDetail;
  newPlan: Plan | null;
  onCancelUpgrade: () => void;
  onProceedUpgrade: () => void;
}

/**
 * Reusable info row used to render label + right-side content.
 * Handles upgrade mode rendering with optional strike-through previous value.
 */
const InfoRow: React.FC<{
  labelKey: string;
  primaryContent: React.ReactNode;
  strikeContent?: React.ReactNode;
  isUpgradeMode?: boolean;
  primaryClass?: string;
}> = ({
  labelKey,
  primaryContent,
  strikeContent,
  isUpgradeMode,
  primaryClass,
}) => {
  return (
    <div className="px-3 py-1 w-full flex-shrink-0 pt-2 pb-2 flex flex-row text-left justify-left items-center">
      <div className="font-poppins text-xs font-medium leading-6 w-1/3 flex ">
        {labelKey}
      </div>

      <div className="w-2/3 flex flex-shrink-0 justify-end">
        <span
          className={`text-right font-poppins text-xs font-semibold leading-6 ${
            primaryClass ?? ""
          }`}
        >
          {!isUpgradeMode ? primaryContent : primaryContent}
        </span>
        {isUpgradeMode && strikeContent && (
          <span className="ml-1 text-right dark:text-white dark:opacity-[0.5] text-[rgba(78,78,78,0.30)] font-poppins text-xs font-semibold leading-6 line-through">
            {strikeContent}
          </span>
        )}
      </div>
    </div>
  );
};

const TransferManagePlan: React.FC<TransferManagePlanProps> = ({
  subscription,
  newPlan,
  onCancelUpgrade,
  onProceedUpgrade,
}) => {
  const { t, i18n } = useTranslation();
  const layout = useFintechLayouts();
  const { data: paymentMethods } = usePaymentMethods();
  const isUpgradeMode = !!newPlan;
  const [isManageMode, setIsManageMode] = useState(false);
  const navigate = useNavigate();
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useContext(AsaStateContext);
  const cancelPlanHandler = () => {
    AnalyticsService.track("ASATransfer Cancel Plan Clicked", AnalyticsAction.Click, AnalyticsFeature.AsaTransfer, {
      subscriptionName: subscription?.subscriptionTitle,
    });
    setState({
      ...state,
      reactivateHeaderText: t("planCanceledOn"),
      reactivateBodyText: t("asaCancelation"),
      cancelPlanDate: Date.now(),
    });
    navigate("/cancelSubscription");
  };
  const cancelHandler = () => {
    if (isManageMode) {
      setIsManageMode(false);
    } else {
      cancelPlanHandler();
    }
  };

  const formatDate = (dateString: string | number | Date) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    const localeToUse = i18n.language || "en-US";
    return new Date(dateString).toLocaleDateString(localeToUse, options);
  };

  const getPaymentMethodLastDigits = () => {
    if (subscription?.paymentType == "ACH") {
      return subscription?.bankAccountNumberLast4;
    }
    return subscription?.cardNumberLast4;
  };

  const getImageSource = (
    type: string | undefined,
    paymMethodCode: number | undefined
  ) => {
    const cardNumber = paymentMethods?.find(
      (item) => item?.asaPaymentMethodCode == paymMethodCode
    )?.cardNumber;
    switch (type) {
      case "ACH":
      case "Bank":
        return check_everyday;
      case "Visa":
        return visa;
      case "CC":
        if (cardNumber?.startsWith("4")) return visa;
        if (cardNumber?.startsWith("3")) return amex;
        if (cardNumber?.startsWith("5")) return city_double_cash;
        return cc_generic;
      default:
        return pwa_image;
    }
  };



  const proceedToggleManage = () => {
    AnalyticsService.track("ASATransfer Manage Plan Clicked", AnalyticsAction.Click, AnalyticsFeature.AsaTransfer, {
      subscriptionName: subscription?.subscriptionTitle,
    });
    setIsManageMode(true);
  };

  useEffect(() => {
 
    if(isManageMode){
      setIsManageMode(false);
    }
  }, [newPlan]);
  return (
    <div
      ref={rowRef}
      className="dark:text-[#D8D8D8] text-[#4E4E4E] w-full flex-shrink-0 pt-2 pb-2 flex flex-col justify-center items-center"
    >
      {/* Plan row */}
      <InfoRow
        labelKey={t("plan")}
        primaryContent={
          !isUpgradeMode
            ? subscription?.subscriptionTitle
            : newPlan?.subscriptionTitle
        }
        strikeContent={!isUpgradeMode ? undefined : subscription?.subscriptionTitle}
        isUpgradeMode={isUpgradeMode}
      />

      {/* Manage mode special */}
      {isManageMode && (
        <div className="px-3 py-1 w-full flex-shrink-0 pt-2 pb-2 flex flex-row text-left justify-left items-center">
          <div className="w-1/3 font-poppins text-xs font-medium leading-6 w-1/2 flex " />
          <div className="w-2/3 flex flex-shrink-0 justify-end"
              onClick={cancelPlanHandler}
            >
            <span className="text-[#705AE2] text-right font-poppins text-xs font-bold leading-6">
              {t("cancelPlan")}
            </span>
          </div>
        </div>
      )}

      {/* Next payment / Today */}
      {!isManageMode && (
        <InfoRow
          labelKey={t("nextPayment")}
          primaryContent={
            !isUpgradeMode
              ? formatDate(subscription?.nextRecurringScheduleDate ?? "")
              : t("today")
          }
          strikeContent={!isUpgradeMode ? undefined : formatDate(subscription?.nextRecurringScheduleDate ?? "")}
          isUpgradeMode={isUpgradeMode}
        />
      )}

      {/* Payment method (custom right side with image) */}
      <div className="px-3 py-1 w-full flex-shrink-0 pt-2 pb-2 flex flex-row text-left justify-left items-center">
        <div className="font-poppins text-xs font-medium leading-6 w-1/2 flex ">
          {t("paymentMethod")}
        </div>

        <div className="w-1/2 flex flex-shrink-0 justify-end">
          <span className="px-1 text-right font-poppins text-xs font-semibold leading-6">
            ****{getPaymentMethodLastDigits()}
          </span>
          <span className="px-1 py-1 bg-white">
            <img
              src={getImageSource(
                subscription?.paymentType,
                subscription?.asaPaymentMethodCode
              )}
              alt={subscription?.paymentType || t("noPaymentMethod")}
              className="w-8 h-5 rounded m"
              style={{ objectFit: "contain" }}
            />
          </span>
        </div>
      </div>

      {/* Total */}
      {!isManageMode && (
        <InfoRow
          labelKey={t("total")}
          primaryContent={
            !isUpgradeMode
              ? `$${subscription?.billingAmount.toFixed(2)}`
              : `$${newPlan?.recurringAmount.toFixed(2)}`
          }
          strikeContent={!isUpgradeMode ? undefined : `$${subscription?.billingAmount.toFixed(2)}`}
          isUpgradeMode={isUpgradeMode}
        />
      )}

      {/* Manage mode change payment */}
      {isManageMode && (
        <div className="px-3 py-1 w-full flex-shrink-0 pt-2 pb-2 flex flex-row text-left justify-left items-center">
          <div className="w-1/3 font-poppins text-xs font-medium leading-6 w-1/2 flex " />
          <div className="w-2/3 flex flex-shrink-0 justify-end"
              onClick={() => {
                AnalyticsService.track("ASATransfer Change Payment Clicked", AnalyticsAction.Click, AnalyticsFeature.AsaTransfer, {
                  subscriptionName: subscription?.subscriptionTitle,
                });
                onProceedUpgrade();
              }}
          >
            <span className="text-[#705AE2] text-right font-poppins text-xs font-bold leading-6">
              {t("changePaymentMethod")}
            </span>
          </div>
        </div>
      )}

      {/* Actions */}
      {!isUpgradeMode ? (
        <div className="flex flex-row w-full justify-center items-center gap-4 mt-4 ">
          <div
            className= { "w-1/2 border dark:border-white border-[#4E4E4E] flex text-center dark:text-[#D8D8D8] text-[#4E4E4E] mx-1 py-3  rounded-lg shadow-md font-poppins font-semibold uppercase justify-center " +(isManageMode?"flex-grow ":"")} 
            onClick={cancelHandler}
          >
            {isManageMode ? t("cancel") : t("cancelPlan")}
          </div>

          {!isManageMode && (
            <div
              className="w-1/2 dark:shadow-none shadow-[0_4px_6px_rgba(120,_120,_120,_0.7)] flex text-center mx-1 py-3 text-white rounded-lg font-poppins font-semibold uppercase justify-center"
              style={layout?.buttonStyle}
              onClick={proceedToggleManage}
            >
              {t("managePlan")}
            </div>
          )}
        </div>
      ) : (
        <div className=" flex flex-row w-full justify-center items-center gap-4 mt-4 ">
          <div
            className="w-1/2 flex-grow justify-center border dark:border-white border-[#4E4E4E] flex text-center dark:text-[#D8D8D8] text-[#4E4E4E] mx-1 py-3  rounded-lg shadow-md font-poppins font-semibold uppercase"
            onClick={() => {
              AnalyticsService.track("ASATransfer Cancel Upgrade Clicked", AnalyticsAction.Click, AnalyticsFeature.AsaTransfer);
              onCancelUpgrade();
            }}
          >
            {t("return")}
          </div>
          <div
            className="w-1/2 flex-grow flex justify-center mx-1 py-3  text-white rounded-lg shadow-md font-poppins font-semibold uppercase"
            style={layout?.buttonStyle}
            onClick={() => {
              AnalyticsService.track("ASATransfer Proceed Upgrade Clicked", AnalyticsAction.Click, AnalyticsFeature.AsaTransfer, {
                subscriptionName: newPlan?.subscriptionTitle,
                price: newPlan?.recurringAmount,
              });
              onProceedUpgrade();
            }}
          >
            {t("next")}
          </div>
        </div>
      )}
    </div>
  );
};

export default TransferManagePlan;